import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import type { TrashModel } from "target_wasm";
import { ModelElementType } from "@/components/entities";
import { AssetIcon } from "@/components/common/AssetIcon";
import { Spinner } from "@/components/common/Spinner";
import { AppearancePicker } from "@/components/common/AppearancePicker";
import { Button } from "@/components/common/Button";
import { Tooltip } from "@/components/common/Tooltip";
import { TooltipButton } from "@/components/common/TooltipButton";
import { SpaceDot } from "@/components/main/spaces/SpaceDot";
import {
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import { EditableName } from "@/components/canvas/utils/EditableName";
import {
  ContextMenu,
  type ContextMenuItem,
} from "@/components/common/ContextMenu";
import { useContextMenu } from "@/components/hooks/useContextMenu";
import {
  cancelOpening,
  notifyRefusal,
  openModel as openModelSession,
  useOpeningModel,
} from "@/components/canvas/modelSession";
import { OpeningSpinner } from "./OpeningSpinner";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useModelActions } from "@/components/main/utils/models/useModelActions";
import { useMoveModelToSpace } from "@/components/main/utils/models/useMoveModelToSpace";
import { trashRiskColor } from "@/components/main/utils/models/types";
import { DeleteModelDialog } from "@/components/main/utils/models/deletion/DeleteModelDialog";
import { useModelAppearanceUpdate } from "@/components/main/utils/models/useModelAppearance";
import { canEditModelMeta } from "@/components/main/utils/models/types";
import { MAX_MODEL_NAME_CHARS } from "@/components/main/utils/models/modelName";
import { ownershipLabel } from "./selectors";
import { LiveIndicator } from "./LiveIndicator";
import { CATALOG, MANAGE_ACCESS_MENU_LABEL } from "@/actions/catalog";

interface ModelRowProps {
  model: ModelElementType;
  /** Right-aligned mono metadata (e.g. compact relative time). */
  meta?: string;
  /** Spells `meta` out on hover, e.g. "Opened 2h ago". */
  metaTooltip?: string;
  /** Show the "N active users" label beside the live dot (All-models list). */
  showActiveCount?: boolean;
  /** Freshly created — plays the settle-in outline animation. */
  isFresh?: boolean;
  /** Offers "Move to space" — the Overview only (D65). */
  movable?: boolean;
  /** Replaces the muted "Owner"/"Shared" word; the trash rendering wins. */
  sub?: React.ReactNode;
  /** Renders a Trash entry: not openable, a countdown for meta, Restore in
   *  place of the star. */
  trash?: {
    countdownLabel: string;
    countdownTooltip: string;
    onRestore: () => void;
    onDeleteForever: () => void;
    /// Absent for a catalog model, which has no org Trash entry to review.
    trashModel?: TrashModel;
    onAnalysisClosed?: () => void;
    /// Set while the model's space is doomed (D53): it comes back with the space.
    restoreLocked?: string;
    deleteForeverLocked?: boolean;
  };
}

const prefersReducedMotion = (): boolean =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Fixed column widths, so meta and star line up across every list. */
export const ModelRow: React.FC<ModelRowProps> = ({
  model,
  meta,
  metaTooltip,
  showActiveCount = false,
  isFresh = false,
  movable = false,
  sub,
  trash,
}) => {
  const navigate = useNavigate();
  const rowRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const menu = useContextMenu();
  const toggleStar = useMainStateStore((state) => state.toggleStar);
  const isStarred = model.is_starred ?? false;
  // A model scheduled for deletion can't be entered — it's on its way to Trash.
  const canOpen = !trash && !model.scheduled_deletion_date;
  const modelId = String(model.model_id);
  const isOpening = useOpeningModel((s) => s.modelId === modelId);
  const canEdit = canOpen && canEditModelMeta(model);
  const pinnable = !model.is_catalog;

  const actions = useModelActions(model);

  const handleAppearanceChange = useModelAppearanceUpdate(model);
  const moveToSpace = useMoveModelToSpace(model, rowRef);

  const commitName = actions.commitName;

  // The Overview stays up while the model loads; the canvas takes over once
  // it is ready, so the canvas never shows a loading screen for a row open.
  const openModel = async (): Promise<void> => {
    if (!canOpen || isEditing || isPickerOpen || isOpening) return;
    if (!navigator.onLine) return;
    // The canvas route waits on its code; fetching it now overlaps the load.
    import("@/components/canvas/CanvasApp").catch(() => {});
    const result = await openModelSession(modelId);
    if (result === "ready") navigate(`/canvas/${modelId}`);
    else if (result !== "cancelled") notifyRefusal(result);
  };

  useEffect(() => () => cancelOpening(modelId), [modelId]);
  useEffect(() => {
    if (!isOpening) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelOpening(modelId);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpening, modelId]);

  const scheduledTableCount =
    trash?.trashModel?.tables_scheduled_for_deletion_count ?? 0;

  const contextMenuItems: ContextMenuItem[] = trash
    ? [
        {
          label: trash.restoreLocked ? "Restore (with its space)" : "Restore",
          icon: <RestoreIcon className="icon-md" />,
          onClick: trash.onRestore,
          disabled: !!trash.restoreLocked,
        },
        {
          label: "Review scheduled tables",
          icon: <TableChartOutlinedIcon className="icon-md" />,
          onClick: () => setIsAnalysisOpen(true),
          disabled: scheduledTableCount === 0,
        },
        { label: "", onClick: () => {}, divider: true },
        {
          label: "Delete forever",
          icon: <DeleteOutlineOutlinedIcon className="icon-md" />,
          onClick: trash.onDeleteForever,
          disabled: trash.deleteForeverLocked,
          danger: true,
        },
      ]
    : [
        {
          label: "Open",
          icon: <OpenInFullIcon className="icon-md" />,
          onClick: () => void openModel(),
          disabled: !canOpen,
        },
        {
          // The new tab opens the model itself, behind its loading screen.
          label: "Open in a new tab",
          icon: <OpenInNewIcon className="icon-md" />,
          onClick: () =>
            window.open(`/canvas/${modelId}`, "_blank", "noopener,noreferrer"),
          disabled: !canOpen,
        },
        { label: "", onClick: () => {}, divider: true },
        {
          label: "Rename",
          icon: <EditOutlinedIcon className="icon-md" />,
          onClick: () => setIsEditing(true),
          disabled: !canEdit,
        },
        {
          label: "Change appearance",
          icon: <PaletteOutlinedIcon className="icon-md" />,
          onClick: () => setIsPickerOpen(true),
          disabled: !canEdit,
        },
        {
          label: MANAGE_ACCESS_MENU_LABEL,
          icon: <CATALOG.manageAccess.Icon className="icon-md" />,
          onClick: actions.openShare,
          disabled: !actions.permissions.canOpenAccess,
        },
        ...(movable
          ? [
              {
                label: "Move to space",
                icon: <DriveFileMoveOutlinedIcon className="icon-md" />,
                onClick: moveToSpace.start,
                disabled: !actions.permissions.canMoveModel,
              },
            ]
          : []),
        {
          label: CATALOG.copyModelId.label,
          icon: <CATALOG.copyModelId.Icon className="icon-md" />,
          onClick: actions.copyModelId,
        },
        { label: "", onClick: () => {}, divider: true },
        {
          label: "Delete",
          icon: <DeleteOutlineOutlinedIcon className="icon-md" />,
          onClick: actions.requestDelete,
          disabled: !actions.permissions.canDeleteModel,
          danger: true,
        },
      ];

  const activeCount = model.active_sessions?.length ?? 0;

  // The popovers are portaled, which takes the pointer off the row and drops
  // `:hover` — pin the row's hover state while any is open.
  const isRowPinned = menu.isOpen || isPickerOpen || moveToSpace.isOpen;

  return (
    <>
      <div
        ref={rowRef}
        className={`group flex items-center gap-2 item-height px-1 rounded-app hover:bg-bg-hover ${
          isRowPinned || isOpening ? "bg-bg-hover" : ""
        } ${canOpen ? "cursor-pointer" : "cursor-default"} ${
          isFresh ? "overview-fresh" : ""
        }`}
        tabIndex={0}
        data-model-id={model.model_id}
        onClick={canOpen ? () => void openModel() : undefined}
        {...menu.triggerProps}
      >
        <div className="relative shrink-0" ref={iconRef}>
          <span
            role={canEdit ? "button" : undefined}
            aria-label={canEdit ? "Change model appearance" : undefined}
            className={`flex items-center justify-center rounded-app p-0.5 ${
              canEdit ? "hover:bg-bg-hover" : ""
            }`}
            onClick={(e) => {
              if (!canEdit || actions.isDeleting || isOpening) return;
              e.stopPropagation();
              // Open-only: the picker ignores anchor mousedowns, so no
              // close-reopen flash.
              setIsPickerOpen(true);
            }}
          >
            {actions.isDeleting ? (
              <Spinner size="sm" />
            ) : isOpening && !prefersReducedMotion() ? (
              <OpeningSpinner color={model.color} />
            ) : (
              <AssetIcon size="sm" color={model.color} iconIndex={model.icon} />
            )}
          </span>
          <AppearancePicker
            currentColor={model.color}
            currentIcon={model.icon}
            onColorChange={(color) => void handleAppearanceChange({ color })}
            onIconChange={(icon) => void handleAppearanceChange({ icon })}
            isOpen={isPickerOpen}
            onClose={() => setIsPickerOpen(false)}
            disabled={!canEdit}
            anchorRef={iconRef}
          />
        </div>
        {/* `min-w-16` keeps the name readable while the space label expands. */}
        <span className="text-fg-medium flex items-center min-w-16 shrink">
          <EditableName
            name={model.name || "Unnamed Model"}
            isEditing={isEditing}
            canEdit={canEdit}
            editTrigger="icon"
            editColor={model.color || undefined}
            setIsEditing={setIsEditing}
            update={() => {}}
            maxLength={MAX_MODEL_NAME_CHARS}
            commit={commitName}
            abort={() => setIsEditing(false)}
          />
        </span>
        {!trash && (
          <span className="text-fg-muted-regular-small shrink-0">
            {isOpening ? "Opening…" : (sub ?? ownershipLabel(model.relation))}
          </span>
        )}
        <span className="flex-1" />
        {trash ? (
          <>
            {/* Tinted by the worst risk among the scheduled tables. */}
            <span className="flex items-center gap-2 shrink-0">
              {scheduledTableCount > 0 && (
                <Tooltip
                  title={`${scheduledTableCount} ${scheduledTableCount === 1 ? "table" : "tables"} scheduled for deletion — review or transfer`}
                  placement="top"
                >
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Review ${scheduledTableCount} scheduled ${scheduledTableCount === 1 ? "table" : "tables"} in ${model.name || "Unnamed Model"}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsAnalysisOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault();
                      e.stopPropagation();
                      setIsAnalysisOpen(true);
                    }}
                    className={`inline-flex items-center gap-1 shrink-0 px-1 py-px rounded-app font-mono select-none cursor-pointer hover:bg-bg-hover transition-colors ${trash.trashModel ? trashRiskColor(trash.trashModel) : ""}`}
                    style={{
                      fontSize: "var(--font-size-x-small)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    <TableChartOutlinedIcon className="icon-xs" />
                    {scheduledTableCount}
                  </span>
                </Tooltip>
              )}
              {/* No fixed width — the Restore column to its right is fixed, so
                  the label's right edge still lines up across rows. */}
              <Tooltip title={trash.countdownTooltip} placement="top">
                <span
                  className="font-mono text-fg-muted shrink-0 cursor-default"
                  style={{
                    fontSize: "var(--font-size-x-small)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {trash.countdownLabel}
                </span>
              </Tooltip>
            </span>
            {/* "Delete forever" is irreversible, so it lives in the menu only. */}
            <span
              className={`flex items-center gap-1 shrink-0 transition-opacity ${
                isRowPinned
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"
              }`}
            >
              <TooltipButton
                title={trash.restoreLocked ?? "Restore"}
                variant="contextual"
                size="xs"
                aria-label="Restore model"
                disabled={!!trash.restoreLocked}
                onClick={(e) => {
                  e.stopPropagation();
                  trash.onRestore();
                }}
              >
                <RestoreIcon />
              </TooltipButton>
            </span>
          </>
        ) : (
          <>
            <LiveIndicator count={activeCount} showCount={showActiveCount} />
            <ModelSpaceLabel model={model} />
            <Tooltip title={metaTooltip}>
              <span
                className="font-mono text-fg-muted text-right shrink-0 w-12"
                style={{
                  fontSize: "var(--font-size-x-small)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {meta ?? ""}
              </span>
            </Tooltip>
            <span
              className={`flex justify-end shrink-0 w-6 transition-opacity ${
                isStarred || isRowPinned
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100 has-[:focus-visible]:opacity-100"
              }`}
            >
              {pinnable && (
                <Button
                  data-star
                  variant="default"
                  size="xs"
                  aria-label={isStarred ? "Unpin model" : "Pin model"}
                  onClick={(e) => {
                    e.stopPropagation();
                    void toggleStar(model.model_id, !isStarred);
                  }}
                >
                  {isStarred ? <StarRoundedIcon /> : <StarBorderOutlinedIcon />}
                </Button>
              )}
            </span>
          </>
        )}
      </div>

      <ContextMenu
        items={contextMenuItems}
        position={menu.position}
        onClose={menu.close}
        ariaLabel={`Actions for ${model.name || "Unnamed Model"}`}
      />

      {/* Mounted only while open: one idle dialog per trash row is overhead. */}
      {trash && isAnalysisOpen && (
        <DeleteModelDialog
          open
          onClose={() => {
            setIsAnalysisOpen(false);
            trash.onAnalysisClosed?.();
          }}
          model={model}
          onDelete={async () => setIsAnalysisOpen(false)}
          isFromTrash
          trashModel={trash.trashModel}
        />
      )}

      {movable && moveToSpace.picker}
      {actions.dialogs}
    </>
  );
};

/// A dot that expands into the space's name on hover; nothing for a space
/// outside the active org's tree.
const ModelSpaceLabel: React.FC<{ model: ModelElementType }> = ({ model }) => {
  const space = useOrgContextStore((s) => s.spaceById(model.space_id));
  if (!space) return null;
  return (
    <span
      className="group/space flex shrink-0 items-center font-mono text-fg-muted"
      style={{ fontSize: "var(--font-size-x-small)" }}
      title={space.name}
    >
      <SpaceDot color={spaceColor(space)} size="sm" />
      <span className="sr-only">{space.name}</span>
      {/* The leading gap lives inside the clipped box, so the collapsed state
          is exactly the dot — no dead space in the row. */}
      <span
        aria-hidden
        className="max-w-0 overflow-hidden pl-1.5 text-ellipsis whitespace-nowrap opacity-0 group-hover/space:max-w-32 group-hover/space:opacity-75"
        style={{
          transitionProperty: "max-width, opacity",
          transitionDuration: "var(--duration-short)",
          transitionTimingFunction: "var(--ease-enter)",
        }}
      >
        {space.name}
      </span>
    </span>
  );
};
